'use client';

import React, { useEffect, useRef } from 'react';
import { Brain, Globe, Award, Users, TrendingUp, Zap } from 'lucide-react';

const pillars = [
  {
    icon: Brain,
    title: 'AI-Powered Learning',
    description: 'Personalised study paths powered by adaptive AI that adjusts to your learning pace, identifies knowledge gaps, and recommends the right content at the right time.',
  },
  {
    icon: Award,
    title: 'Accreditation in Progress',
    description: 'Our programmes are designed with NQF alignment in mind. We are transparent about where we are in the accreditation journey — and committed to completing it.',
  },
  {
    icon: Users,
    title: 'Taught by Practitioners',
    description: 'Learn from people who are active in the industry right now — developers, investment professionals, estate agents and property attorneys who understand the realities of the market.',
  },
  {
    icon: Globe,
    title: 'Pan-African Perspective',
    description: 'Join a growing community of students across Africa. Access cross-border investment insights, regional market perspectives, and a continental professional network.',
  },
  {
    icon: TrendingUp,
    title: 'Education Designed for Real Careers',
    description: 'Every programme is built around outcomes that matter in the real world — helping you build a meaningful career, grow a business, or create lasting wealth through property.',
  },
  {
    icon: Zap,
    title: 'Built Around Your Life',
    description: 'Study at your own pace around your career. Self-paced modules, live masterclasses, and weekend intensives designed for working professionals who cannot pause their ambitions.',
  },
];

export default function WhyReusa() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const cards = ref?.current?.querySelectorAll('.scroll-fade, .section-reveal, .stagger-child');
    cards?.forEach((card) => observer?.observe(card));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="why-reusa" className="section-pad-lg bg-background" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        {/* Header */}
        <div className="max-w-2xl mb-16 section-reveal">
          <div className="flex items-center gap-3 mb-5">
            <div className="divider-gold" />
            <span className="label-caps">Why Choose REUSA</span>
          </div>
          <h2 className="font-serif text-section-title text-foreground mb-5">
            Education Built for the
            <br />
            <span className="italic text-gradient-gold">African Real Estate Century</span>
          </h2>
          <p className="font-sans text-base text-muted-foreground leading-relaxed">
            REUSA was built because the real estate industry needed an institution that genuinely understood Africa — its markets, its people, and the real challenges practitioners face every day.
          </p>
        </div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {pillars?.map((pillar, i) => (
            <div
              key={`pillar-${pillar?.title}`}
              className="stagger-child card-luxury p-8 rounded-2xl bg-card luxury-shadow group cursor-default"
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300">
                <pillar.icon size={22} className="text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-serif font-semibold text-xl text-foreground mb-3">
                {pillar?.title}
              </h3>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                {pillar?.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}